import React, { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { BorderedCard } from 'components/BorderedCard';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload';

import { concatWithDot } from 'helpers/string';

import {
  COMPANY_QUALITIES_NAMES,
  COMPETITIVE_ANALYSIS_MAX_LENGTHS,
  STRENGTH_NAMES,
  strengthValues,
  WEAKNESSES_NAMES,
  weaknessesValues,
} from '../utils/form';

import styles from './CompetitorsQualities.module.scss';

interface IProps {
  name: string;
  disabled: boolean;
  title: string;
  className?: string;
  isOurCompany?: boolean;
}

export const CompetitorsQualities: React.FC<IProps> = memo(({ name, disabled = false, title, className = '' }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const strengthName = concatWithDot(name, COMPANY_QUALITIES_NAMES.STRENGTH);
  const weaknessesName = concatWithDot(name, COMPANY_QUALITIES_NAMES.WEAKNESSES);
  const companyLogo = concatWithDot(name, COMPANY_QUALITIES_NAMES.FILE);
  const companyName = concatWithDot(name, COMPANY_QUALITIES_NAMES.NAME);

  const strengthPrefix = STRENGTH_NAMES.STRENGTH;
  const weaknessPrefix = WEAKNESSES_NAMES.WEAKNESSES;

  return (
    <BorderedCard borderCardStyles={{ container: className }}>
      <Grid container spacing={2}>
        <Grid item xs="auto">
          <TooltipFormImageUpload width={64} height={64} placeholder="Logo" name={companyLogo} disabled={disabled} />
        </Grid>
        <Grid item xs>
          <TextInputControl
            control={control}
            id={companyName}
            name={companyName}
            label={title}
            disabled={disabled}
            maxLength={42}
          />
        </Grid>
        <Grid item xs={12}>
          <FormSectionFilledCard>
            <FieldArrayComponent
              fieldArrayStyles={{ removeButton: styles.removeButton }}
              name={strengthName}
              initialItem={strengthValues}
              buttonText={t('marketAnalysisForm.competitors.qualities.strength')}
              disabled={disabled}
              maxLength={4}
            >
              {(namePrefix) => (
                <TextInputControl
                  control={control}
                  id={concatWithDot(namePrefix, strengthPrefix)}
                  name={concatWithDot(namePrefix, strengthPrefix)}
                  label={t('marketAnalysisForm.competitors.qualities.strength')}
                  disabled={disabled}
                  multiline
                  maxLength={COMPETITIVE_ANALYSIS_MAX_LENGTHS.COMPETITOR.STRENGTH}
                />
              )}
            </FieldArrayComponent>
          </FormSectionFilledCard>
        </Grid>
        <Grid item xs={12}>
          <FormSectionFilledCard>
            <FieldArrayComponent
              fieldArrayStyles={{ removeButton: styles.removeButton }}
              name={weaknessesName}
              initialItem={weaknessesValues}
              buttonText={t('marketAnalysisForm.competitors.qualities.weakness')}
              disabled={disabled}
              maxLength={4}
            >
              {(namePrefix) => (
                <TextInputControl
                  control={control}
                  id={concatWithDot(namePrefix, weaknessPrefix)}
                  name={concatWithDot(namePrefix, weaknessPrefix)}
                  label={t('marketAnalysisForm.competitors.qualities.weakness')}
                  disabled={disabled}
                  multiline
                  maxLength={COMPETITIVE_ANALYSIS_MAX_LENGTHS.COMPETITOR.WEAKNESSES}
                />
              )}
            </FieldArrayComponent>
          </FormSectionFilledCard>
        </Grid>
      </Grid>
    </BorderedCard>
  );
});
